import { add, money, zero, type Money } from '@lensmira/domain'

/**
 * Money formatting and parsing, shared by every screen. Appendix D `MR-01`; `LOC-004`.
 *
 * Money crosses the wire as `{ minor: string, currency: string }`: JSON has no `bigint`
 * (`JSON.stringify` throws on one, and the write queue's transport does JSON-stringify the body),
 * so the minor-unit total travels as a digit string that may exceed `Number.MAX_SAFE_INTEGER`.
 * This file is the one place that turns that string into an exact `bigint` and back, so no screen
 * invents its own parsing, no screen calls `Number()` on a money value, and no screen enqueues a
 * write with a raw `bigint` sitting in the body where `JSON.stringify` will fail on it.
 *
 * The arithmetic on an already-parsed amount, `add`, `zero`, and the same-currency check, already
 * lives in `packages/domain`'s `money.ts` and is reused here rather than re-derived, per this
 * repo's `CLAUDE.md`: `domain` is the authority and a shortcut against it costs weeks later. This
 * file adds only what a UI needs and `domain` deliberately does not have: turning a string a
 * shopkeeper typed, or a string that came off the wire, into exact minor units, and turning minor
 * units back into a string a shopkeeper reads, or back onto the wire. Rounding stays out of both,
 * per `MR-04`: nothing here rounds, because nothing here produces a fraction of a minor unit that
 * would need it.
 */

export type { Money }

/** Money as it crosses the wire. JSON has no `bigint`, so the minor-unit total is a digit string. */
export interface WireMoney {
  readonly minor: string
  readonly currency: string
}

export class MoneyInputError extends Error {}

/**
 * Minor-unit decimal places by ISO 4217 code. Defaults to 2, which is correct for PKR and for
 * every other currency this product currently touches (`ACC-005`'s tenant base currency and the
 * USD Paddle price list). The two listed exceptions exist so the default is never silently wrong
 * for a currency this module does end up handling.
 */
const MINOR_DECIMALS: Readonly<Record<string, number>> = {
  JPY: 0,
  KWD: 3,
}

function decimalsFor(currency: string): number {
  return MINOR_DECIMALS[currency] ?? 2
}

const MINOR_UNIT_STRING = /^-?\d+$/

/**
 * A minor-unit string off the wire, exactly, to a `bigint`. `BigInt()` alone would already refuse
 * `"12.5"` or `""`, but its own error names neither the field nor the value, so the shape is
 * checked first for a message a shopkeeper's error banner can show.
 */
export function parseMinorUnits(minor: string): bigint {
  if (!MINOR_UNIT_STRING.test(minor)) {
    throw new MoneyInputError(`Not a whole number of minor units: ${JSON.stringify(minor)}`)
  }
  return BigInt(minor)
}

/**
 * The digit form of a `bigint` amount, at the currency's own decimal places. Built from `bigint`
 * division and modulo throughout, so a value above `Number.MAX_SAFE_INTEGER` never passes through
 * `Number` to get formatted.
 *
 * **Bare on purpose**: no grouping, no currency code. It is the engine {@link formatMoneyForDisplay}
 * and `OrderDetail`'s price-explanation formatter both build on, not the thing a screen shows.
 */
export function formatMoney(minor: bigint, currency: string): string {
  const decimals = decimalsFor(currency)
  const negative = minor < 0n
  const magnitude = negative ? -minor : minor
  const scale = 10n ** BigInt(decimals)
  const whole = magnitude / scale
  const fraction = magnitude % scale
  const fractionPart = decimals > 0 ? `.${fraction.toString().padStart(decimals, '0')}` : ''
  return `${negative ? '-' : ''}${whole.toString()}${fractionPart}`
}

/**
 * Thousands separators on a digit string. The single copy in this workspace, which is the point:
 * `packages/db/test/workspace-conventions.test.ts` fails if this regex appears anywhere else under
 * `apps/counter/src`.
 *
 * On the digit string and never `Intl.NumberFormat` or `toLocaleString`, for the reason `D-03` and
 * `TY-03` give: a locale-driven format can substitute Eastern Arabic digits, and the counter's rule
 * is Western digits everywhere including the Urdu interface.
 *
 * Groups of three, not the South Asian 2-2-3 lakh convention. Neither `PRD.md` nor
 * `DESIGN-TOKENS.md` names a grouping style, and threes is what the app already encoded in five
 * places and about twenty assertions, so it is recorded as the rule rather than chosen fresh. Worth
 * putting to the pilot shop, since a Karachi shopkeeper may well read 1,25,000 faster than 125,000.
 */
export function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * **The one way this app shows an amount of money.** `LOC-004`: currency code, space, sign, then
 * the whole part grouped in threes, at the currency's own decimal places. `PKR 7,500.00`.
 *
 * It exists because the app answered this question five times independently and got two answers:
 * the order list and the order detail grouped, the cash drawer and the new order screen did not, so
 * the same PKR 7,500 read as `PKR 7,500.00` on one screen and `PKR 7500.00` on the Z-report for the
 * same sale. Two of those copies were worse than inconsistent: `TakePayment` and `JobDetail` never
 * imported this module at all and divided by a hardcoded `100n`, so `decimalsFor` never ran and a
 * JPY or KWD amount rendered at the wrong scale entirely, which is an `MR-01` defect rather than a
 * cosmetic one. `TakePayment` also wrote a U+2212 MINUS SIGN before the currency code where every
 * other screen writes an ASCII hyphen after it.
 *
 * Same reasoning as `PROGRESS.md` problem 100 and `apps/api/src/orders/outstanding.ts`: divergence
 * should need an edit to one file rather than being merely discouraged.
 */
export function formatMoneyForDisplay(minor: bigint, currency: string): string {
  const digits = formatMoney(minor, currency)
  const negative = digits.startsWith('-')
  const magnitude = negative ? digits.slice(1) : digits
  const [wholePart = '0', fractionPart] = magnitude.split('.')
  const fraction = fractionPart === undefined ? '' : `.${fractionPart}`
  return `${currency} ${negative ? '-' : ''}${groupThousands(wholePart)}${fraction}`
}

/**
 * An amount in an **editable** money field, which is a different job from showing one.
 *
 * `formatMoneyForDisplay` groups thousands and prefixes the currency code, and `parseMajorInput`
 * refuses both, so a screen that pre-fills an editable amount with the display form hands the
 * shopkeeper a value that same screen then calls "Not a valid amount". The promotions revision form
 * did exactly that, and `condition-builder.test.ts` found it by round-tripping a condition with
 * PKR 15,000 in it.
 *
 * Bare digits at the currency's own scale, which is what the input takes back. It delegates to
 * `formatMoney` and exists so a screen never imports that one directly: the workspace convention
 * test bans that import, because the last screen to use it displayed `PKR 1500.50` while the rest
 * of the app displayed `PKR 1,500.50`. This is for a value somebody is about to edit, never for a
 * value somebody is only going to read.
 */
export function formatMinorForInput(minor: bigint, currency: string): string {
  return formatMoney(minor, currency)
}

/** The same, from a wire string. Throws `MoneyInputError` on anything that is not minor units. */
export function formatWireForDisplay(minor: string, currency: string): string {
  return formatMoneyForDisplay(parseMinorUnits(minor), currency)
}

/**
 * What a shopkeeper types in a money field (`DESIGN-TOKENS.md` §16.1's money input), to exact
 * minor units. Parsed digit by digit against a pattern, never through `Number()` or `parseFloat`,
 * per `MR-01`: a `number` only approximates an amount once it has enough digits, and a till has no
 * acceptable amount of approximation. The fraction is capped at the currency's own decimal places,
 * so `"0.005"` on a two-decimal currency is refused rather than silently truncated.
 */
export function parseMajorInput(input: string, currency: string): bigint {
  const decimals = decimalsFor(currency)
  const trimmed = input.trim()
  if (trimmed === '') {
    throw new MoneyInputError('Amount is required')
  }

  const fractionGroup = decimals > 0 ? `(?:\\.(\\d{1,${decimals}}))?` : ''
  const pattern = new RegExp(`^(-)?(\\d+)${fractionGroup}$`)
  const match = pattern.exec(trimmed)
  if (!match) {
    throw new MoneyInputError(`Not a valid amount: ${JSON.stringify(input)}`)
  }

  const negative = match[1] === '-'
  const whole = BigInt(match[2] as string)
  const scale = 10n ** BigInt(decimals)
  const fractionText = (match[3] ?? '').padEnd(decimals, '0')
  const fraction = decimals > 0 ? BigInt(fractionText) : 0n
  const magnitude = whole * scale + fraction
  return negative ? -magnitude : magnitude
}

/**
 * A wire value to the `Money` the rest of the app works with, for a screen reading a response.
 * Reuses `domain`'s `money()` constructor, so a currency code the server would refuse is refused
 * here too, in the same words.
 */
export function fromWire(wire: WireMoney): Money {
  return money(parseMinorUnits(wire.minor), wire.currency)
}

/**
 * A `Money` back to the wire shape, for a screen building an `enqueue` body. Not optional glue:
 * the write queue's transport `JSON.stringify`s the body, and `JSON.stringify` throws on a
 * `bigint`, so a screen that enqueues `{ total: someMoneyValue }` instead of
 * `{ total: toWire(someMoneyValue) }` fails at send time, long after the mistake was made.
 */
export function toWire(amount: Money): WireMoney {
  return { minor: amount.minor.toString(), currency: amount.currency }
}

/**
 * The sum of several tender amounts in the same currency. This is only the addition: `MR-08`'s
 * split-tender allocation, where the residual goes to the last tender, belongs to whichever screen
 * collects the tenders and decides that order. Reuses `domain`'s `add`, so a tender in the wrong
 * currency is refused the same way anywhere else in the app that adds two `Money` values is: by
 * throwing, not by being silently skipped or coerced.
 */
export function sumTenders(currency: string, amounts: readonly Money[]): Money {
  return amounts.reduce((total, amount) => add(total, amount), zero(currency))
}
